import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { FormSearch } from 'app/models/dashboard/FormStatus';
import { ProjectManagementView } from 'app/models/form/ProjectManagementView';
import { BaseDashboard } from 'app/models/dashboard/BaseDashboard';
import { UtilityService } from 'app/services/utility.service';
import { environment } from 'environments/environment';
import { ApInvoiceDuplicateSearchRequest } from 'app/models/form/ApInvoiceDuplicateSearchRequest';

@Injectable({
	providedIn: 'root',
})
export class VoidReissueFormService {
	private readonly urlPath = 'void-reissue-form';

	constructor(private readonly httpClient: HttpClient, private readonly utilityService: UtilityService) {}

	createForm(clientId: string, form: any): Observable<any> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}`, this.setupRequest(form))
			.pipe();
	}
	private setupRequest(request: any) {
		const formData: FormData = new FormData();
		formData.append('model', JSON.stringify(request));

		return formData;
	}
	updateForm(clientId: string, formId: string, request: any): Observable<any> {
		return this.httpClient
			.put<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}`, this.setupRequest(request))
			.pipe();
	}

	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		const url = `${environment.apiUrl}forms/clients/${clientId}/${
			this.urlPath
		}/users/${userId}?${this.utilityService.objectToQueryString(search)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	getForm(clientId: string, id: string): Observable<any> {
		return this.httpClient.get<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}`).pipe();
	}
	getReport(clientId: string): Observable<any> {
		return this.httpClient
			.get<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/void-re-issue-checks-report`)
			.pipe();
	}
	deleteDocument(clientId: string, formId: any, docId: any): Observable<any> {
		return this.httpClient.delete<any>(
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/docs/${docId}`
		);
	}
	rejectForm(
		clientId: string,
		formId: string,
		rejectionReason: { reason: string; workflowId: number }
	): Observable<any> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/decline`, rejectionReason)
			.pipe();
	}

	approveForm(clientId: string, formId: string, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/approve`, { userId })
			.pipe();
	}

	sendBack(clientId: string, formId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/review`, {})
			.pipe();
	}

	uploadFile(clientId: string, id, file: File): Observable<HttpEvent<any>> {
		const formData: FormData = new FormData();
		formData.append('file', file);
		const req = new HttpRequest(
			'POST',
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}/docs`,
			formData,
			{
				reportProgress: true,
				responseType: 'json',
			}
		);
		return this.httpClient.request(req);
	}

	getAll(clientId: string, status = 'Approved'): Observable<ProjectManagementView[]> {
		const url = `${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}?status=${status}`;
		return this.httpClient.get<ProjectManagementView[]>(url).pipe();
	}

	searchForPayment(
		clientId: string,
		request: ApInvoiceDuplicateSearchRequest,
		getVoidRecoreds: boolean
	): Observable<any[]> {
		return this.httpClient
			.post<any[]>(
				`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/search-for-payment/${getVoidRecoreds}`,
				request
			)
			.pipe();
	}
	getCombinedPayment(clientId: string, checkNo: string): Observable<any[]> {
		return this.httpClient
			.get<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/get-combined-payments/${checkNo}`)
			.pipe();
	}
	submitForApproval(clientId: string, id: any, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}/approval`, {
				userId,
			})
			.pipe();
	}

	getLinkedVoidReissueFormURL(clientId: string, sourceId: any): Observable<any> {
		return this.httpClient
			.get<any>(
				`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/linked-void-reissue-form-url/${sourceId}`
			)
			.pipe();
	}

	// Batch of getLinkedVoidReissueFormURL: one request returns the link for every source id,
	// keyed by source id (an empty string when there is no linked form).
	getLinkedVoidReissueFormURLs(clientId: string, sourceIds: string[]): Observable<Record<string, string>> {
		return this.httpClient
			.post<Record<string, string>>(
				`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/linked-void-reissue-form-urls`,
				{ sourceIds }
			)
			.pipe();
	}

	getPaymentDetails(clientId: string, sourceId: string): Observable<any> {
		return this.httpClient
			.get<any>(`${environment.apiUrl}forms/clients/${clientId}/void-reissue-form/get-payment-details/${sourceId}`)
			.pipe();
	}
}
